import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Avatar,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Visibility,
  Download,
  Edit,
  Delete,
  Add,
  Search,
  FilterList,
  CheckCircle,
  Schedule,
  Warning,
  Assignment,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../contexts/NotificationContext';
import { applicationService, Application as BaseApplication } from '../../services/applicationService';

// Extended interfaces for UI display
interface UIFees {
  baseFee: number;
  processingFee: number;
  serviceFee: number;
  total: number;
  currency: string;
  paid?: number;
}

interface UIDocuments {
  required: number;
  uploaded: number;
}

interface UIApplication extends Omit<BaseApplication, 'fees' | 'documents'> {
  destination?: string;
  submissionDate?: string;
  expectedProcessingTime?: string;
  progress?: number;
  applicationType?: string;
  referenceNumber?: string;
  fees: UIFees;
  documents: UIDocuments;
}

const ApplicationPage: React.FC = () => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  
  const [applications, setApplications] = useState<UIApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState<UIApplication | null>(null);
  const [viewDetailsDialog, setViewDetailsDialog] = useState(false);

  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await applicationService.getApplications();
      if (response.success && response.data) {
        // Transform the data to match UI interface
        // Map status to realistic progress percentage
        const statusProgress: Record<string, number> = {
          draft: 10, pending: 30, in_review: 60, approved: 100, rejected: 100
        };
        const transformedApplications = response.data.map((app: BaseApplication): UIApplication => ({
          ...app,
          destination: app.toCountry,
          submissionDate: app.submittedAt,
          expectedProcessingTime: app.status === 'approved' ? 'Complete' : '15-20 business days',
          progress: statusProgress[app.status] || 0,
          applicationType: app.visaType,
          referenceNumber: `TN-${app.id.slice(0, 6).toUpperCase()}`,
          fees: {
            ...app.fees,
            paid: app.status === 'approved' ? app.fees.total : 0
          },
          documents: {
            required: app.documents.length || 0,
            uploaded: app.documents.length
          }
        }));
        setApplications(transformedApplications);
      } else {
        showError(response.message || 'Failed to fetch applications');
      }
    } catch (error) {
      showError('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleDeleteApplication = async (applicationId: string) => {
    if (window.confirm('Are you sure you want to delete this application?')) {
      try {
        const response = await applicationService.deleteApplication(applicationId);
        if (response.success) {
          showSuccess('Application deleted successfully');
          fetchApplications();
        } else {
          showError(response.message || 'Failed to delete application');
        }
      } catch (error) {
        showError('Failed to delete application');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      case 'in_review': return 'info';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircle />;
      case 'rejected': return <Warning />;
      case 'pending': return <Schedule />;
      case 'in_review': return <Assignment />;
      default: return <Schedule />;
    }
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = (app.destination?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.visaType.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.referenceNumber?.toLowerCase().includes(searchTerm.toLowerCase())) ?? false;
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const applicationsByStatus = {
    all: applications.length,
    pending: applications.filter(app => app.status === 'pending').length,
    in_review: applications.filter(app => app.status === 'in_review').length,
    approved: applications.filter(app => app.status === 'approved').length,
    rejected: applications.filter(app => app.status === 'rejected').length,
    draft: applications.filter(app => app.status === 'draft').length,
  };

  const ApplicationDetailsDialog = () => (
    <Dialog
      open={viewDetailsDialog}
      onClose={() => setViewDetailsDialog(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Application Details - {selectedApplication?.destination}
      </DialogTitle>
      <DialogContent>
        {selectedApplication && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Reference Number
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedApplication.referenceNumber || 'Not assigned'}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Visa Type
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedApplication.visaType}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Destination
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedApplication.destination || selectedApplication.toCountry}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Submitted
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedApplication.submissionDate
                  ? new Date(selectedApplication.submissionDate).toLocaleDateString()
                  : 'Not yet submitted'
                }
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Status
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Chip
                  icon={getStatusIcon(selectedApplication.status)}
                  label={selectedApplication.status.replace('_', ' ').toUpperCase()}
                  color={getStatusColor(selectedApplication.status) as any}
                />
              </Box>

              <Typography variant="subtitle2" color="text.secondary">
                Progress
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={selectedApplication.progress}
                  sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2">
                  {selectedApplication.progress}%
                </Typography>
              </Box>

              <Typography variant="subtitle2" color="text.secondary">
                Documents
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedApplication.documents.uploaded} of {selectedApplication.documents.required} uploaded
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Fees
              </Typography>
              <Typography variant="body1">
                {selectedApplication.fees.paid} / {selectedApplication.fees.total} {selectedApplication.fees.currency}
                {(selectedApplication.fees.paid ?? 0) < selectedApplication.fees.total && (
                  <Chip label="Payment Pending" color="warning" size="small" sx={{ ml: 1 }} />
                )}
              </Typography>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setViewDetailsDialog(false)}>Close</Button>
        <Button
          variant="contained"
          startIcon={<Edit />}
          onClick={() => {
            setViewDetailsDialog(false);
            if (selectedApplication) navigate(`/applications/${selectedApplication.id}`);
          }}
        >
          View Full Details
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <LinearProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            My Applications
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track and manage all your visa applications in one place
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/visa-check')}
        >
          New Application
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {applicationsByStatus.all}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
                {applicationsByStatus.pending}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'info.main' }}>
                {applicationsByStatus.in_review}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                In Review
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                {applicationsByStatus.approved}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Approved
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>
                {applicationsByStatus.rejected}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Rejected
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'grey.600' }}>
                {applicationsByStatus.draft}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Draft
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by destination, visa type, or reference number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              select
              label="Filter by Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="in_review">In Review</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Applications Table */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Destination</TableCell>
                <TableCell>Visa Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredApplications.map((application) => (
                <TableRow key={application.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.100', color: 'primary.main', width: 40, height: 40 }}>
                        {(application.destination || application.toCountry).charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>
                          {application.destination}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {application.applicationType}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{application.visaType}</TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(application.status)}
                      label={application.status.replace('_', ' ').toUpperCase()}
                      color={getStatusColor(application.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
                      <LinearProgress
                        variant="determinate"
                        value={application.progress}
                        sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                      />
                      <Typography variant="body2">
                        {application.progress}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {application.submissionDate ? new Date(application.submissionDate).toLocaleDateString() : 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {application.expectedProcessingTime}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {application.referenceNumber || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedApplication(application);
                          setViewDetailsDialog(true);
                        }}
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/applications/${application.id}`)}
                        title="Download"
                      >
                        <Download />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/applications/${application.id}`)}
                        title="Edit"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteApplication(application.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredApplications.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Assignment sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No applications found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria' 
                : 'Start your first visa application today'
              }
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/visa-check')}
            >
              New Application
            </Button>
          </Box>
        )}
      </Paper>

      <ApplicationDetailsDialog />
    </Container>
  );
};

export default ApplicationPage;
